import { type ReactNode, useState } from 'react';
import {
  Badge,
  Button,
  Divider,
  Drawer,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { FeatureFlagResponse } from './useAdminFeatureFlags';
import { TenantColumn } from './FlagMatrixGrid';
import { useFlagDetail, useFlagSimulation } from './useFlagMatrix';
import { CellState, FlagDetail, STATE_META } from './flagMatrixTypes';

export type FlagMatrixSelection =
  | { kind: 'cell'; flagKey: string; tenantName: string }
  | { kind: 'flag'; flagKey: string }
  | { kind: 'tenant'; tenantName: string };

type Props = {
  selection: FlagMatrixSelection | null;
  flags: FeatureFlagResponse[];
  tenants: TenantColumn[];
  stateFor: (flagKey: string, tenantName: string) => CellState;
  onSelectCell: (flagKey: string, tenantName: string) => void;
  onClose: () => void;
};

const BREAKDOWN_GROUPS: [CellState, string][] = [
  ['targeted_on', 'Targeted on'],
  ['targeted_off', 'Targeted off'],
  ['on_default_true', 'Default true'],
  ['on_default_false', 'Default false'],
  ['off', 'Off'],
  ['unknown', 'Non-boolean'],
  ['failed', 'Evaluation failed'],
];

function serveColour(value: unknown): string {
  if (value === true) return 'green';
  if (value === false) return 'red';
  return 'gray';
}

function tenantColumnFor(tenants: TenantColumn[], tenantName: string): TenantColumn {
  return (
    tenants.find(tenant => tenant.tenantName === tenantName) ?? {
      tenantName,
      label: tenantName,
      internal: false,
      error: null,
    }
  );
}

function selectionTitle(selection: FlagMatrixSelection | null): string {
  if (!selection) return '';
  if (selection.kind === 'tenant') return selection.tenantName;
  return selection.flagKey;
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text size="xs" fw={650} tt="uppercase" c="dimmed">
      {children}
    </Text>
  );
}

function StateBadge({ state }: { state: CellState }) {
  const meta = STATE_META[state];
  return (
    <Badge color={meta.colour.split('.')[0]} variant="filled">
      {state === 'failed' ? meta.label : `${meta.label} · serves ${String(meta.onValue)}`}
    </Badge>
  );
}

function ConfigBlock({ detail }: { detail: FlagDetail }) {
  const rows: [string, string][] = [
    ['Kill switch', detail.on ? 'ON' : 'OFF'],
    ['Default', String(detail.fallthroughValue)],
    ['Off variation', String(detail.offValue)],
  ];
  return (
    <Stack gap={4}>
      {rows.map(([label, value]) => (
        <Group key={label} justify="space-between">
          <Text size="sm" c="dimmed">
            {label}
          </Text>
          <Text size="sm" ff="monospace">
            {value}
          </Text>
        </Group>
      ))}
      {detail.prerequisiteKeys.length > 0 && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Prerequisites
          </Text>
          <Text size="sm" ff="monospace">
            {detail.prerequisiteKeys.join(', ')}
          </Text>
        </Group>
      )}
    </Stack>
  );
}

function RulesBlock({ detail }: { detail: FlagDetail }) {
  if (!detail.on) {
    return (
      <Text size="sm" c="dimmed">
        Flag is off — no rules evaluated. Every tenant serves false.
      </Text>
    );
  }
  if (detail.rules.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No rules. Every tenant falls through to the default ({String(detail.fallthroughValue)}).
      </Text>
    );
  }
  return (
    <Stack gap="xs">
      {detail.rules.map((rule, index) => (
        <Paper key={`${index}-${rule.description ?? ''}`} withBorder p="xs" radius="sm">
          <Group gap="xs" mb={4}>
            <Badge size="sm" variant="light" color={serveColour(rule.serveValue)}>
              serves {String(rule.serveValue)}
            </Badge>
            {rule.description && <Text size="sm">{rule.description}</Text>}
          </Group>
          {rule.clauses.map((clause, clauseIndex) => (
            <Text key={clauseIndex} size="xs" ff="monospace" c="dimmed">
              {clause.negate ? 'not ' : ''}
              {clause.attribute} {clause.op} [{clause.values.join(', ')}]
            </Text>
          ))}
        </Paper>
      ))}
      <Text size="xs" c="dimmed">
        Otherwise falls through to the default ({String(detail.fallthroughValue)}).
      </Text>
    </Stack>
  );
}

function FlagConfiguration({ flagKey }: { flagKey: string }) {
  const { detail, isLoading, error } = useFlagDetail(flagKey);

  if (isLoading) return <Loader size="sm" />;
  if (error) {
    return (
      <Text size="sm" c="red">
        Could not load the configuration for {flagKey}: {error.message}
      </Text>
    );
  }
  if (!detail) return null;

  return (
    <>
      <Divider label="Configuration" labelPosition="left" />
      <ConfigBlock detail={detail} />
      <Divider label="Targeting rules" labelPosition="left" />
      <RulesBlock detail={detail} />
    </>
  );
}

function SimulateForm({ flagKey, fixedTenant }: { flagKey: string; fixedTenant: string | null }) {
  const [tenant, setTenant] = useState(fixedTenant ?? '');
  const [userId, setUserId] = useState('');
  const simulate = useFlagSimulation();

  const run = () => {
    if (tenant) {
      simulate.mutate({
        tenantName: tenant,
        userId: userId || undefined,
      });
    }
  };

  const result = simulate.data?.find(item => item.key === flagKey);

  return (
    <Stack gap="xs">
      {fixedTenant === null && (
        <TextInput
          label="Tenant"
          value={tenant}
          onChange={event => setTenant(event.currentTarget.value)}
          placeholder="tenant slug"
        />
      )}
      <TextInput
        label="User id (optional)"
        value={userId}
        onChange={event => setUserId(event.currentTarget.value)}
      />
      <Button onClick={run} loading={simulate.isPending} disabled={!tenant}>
        Simulate{tenant ? ` in ${tenant}` : ''}
      </Button>
      <Text size="xs" c="dimmed">
        Assumes production. Sends {'{ tenant, user-id, environment }'} to LaunchDarkly.
      </Text>
      {simulate.error && (
        <Text size="sm" c="red">
          Simulation failed: {simulate.error.message}
        </Text>
      )}
      {simulate.data && !result && (
        <Text size="sm" c="dimmed">
          No boolean result returned for this flag.
        </Text>
      )}
      {result && (
        <Group gap="sm">
          <StateBadge state={result.state} />
        </Group>
      )}
    </Stack>
  );
}

function CellBody({
  flagKey,
  tenant,
  stateFor,
}: {
  flagKey: string;
  tenant: TenantColumn;
  stateFor: (flagKey: string, tenantName: string) => CellState;
}) {
  return (
    <Stack gap="md">
      <StateBadge state={stateFor(flagKey, tenant.tenantName)} />
      {tenant.error && (
        <Text size="sm" c="red">
          {tenant.tenantName} could not be evaluated: {tenant.error}
        </Text>
      )}
      <FlagConfiguration flagKey={flagKey} />
      <Divider label="Simulate a user" labelPosition="left" />
      <SimulateForm
        key={`${flagKey}-${tenant.tenantName}`}
        flagKey={flagKey}
        fixedTenant={tenant.tenantName}
      />
    </Stack>
  );
}

function FlagBody({
  flagKey,
  tenants,
  stateFor,
}: {
  flagKey: string;
  tenants: TenantColumn[];
  stateFor: (flagKey: string, tenantName: string) => CellState;
}) {
  return (
    <Stack gap="md">
      <FlagConfiguration flagKey={flagKey} />
      <Divider label="Tenant breakdown" labelPosition="left" />
      <Stack gap="sm">
        {BREAKDOWN_GROUPS.map(([state, label]) => {
          const members = tenants.filter(tenant => stateFor(flagKey, tenant.tenantName) === state);
          if (members.length === 0) return null;
          const showChips =
            state === 'targeted_on' || state === 'targeted_off' || state === 'failed';
          return (
            <Stack key={state} gap={4}>
              <Group gap="xs">
                <SectionLabel>{label}</SectionLabel>
                <Badge size="xs" variant="light">
                  {members.length}
                </Badge>
              </Group>
              {showChips ? (
                <Group gap={4}>
                  {members.slice(0, 50).map(tenant => (
                    <Badge key={tenant.tenantName} size="sm" variant="outline" color="gray">
                      {tenant.tenantName}
                    </Badge>
                  ))}
                  {members.length > 50 && (
                    <Badge size="sm" variant="light">
                      +{members.length - 50}
                    </Badge>
                  )}
                </Group>
              ) : (
                <Text size="xs" c="dimmed">
                  {members.length} tenants (fallthrough).
                </Text>
              )}
            </Stack>
          );
        })}
      </Stack>
      <Divider label="Simulate a user" labelPosition="left" />
      <SimulateForm key={flagKey} flagKey={flagKey} fixedTenant={null} />
    </Stack>
  );
}

function TenantBody({
  tenant,
  flags,
  stateFor,
  onSelectCell,
}: {
  tenant: TenantColumn;
  flags: FeatureFlagResponse[];
  stateFor: (flagKey: string, tenantName: string) => CellState;
  onSelectCell: (flagKey: string, tenantName: string) => void;
}) {
  const tenantName = tenant.tenantName;

  if (tenant.error) {
    return (
      <Text size="sm" c="red">
        {tenantName} could not be evaluated: {tenant.error}. Its flag states are unknown, not off.
      </Text>
    );
  }

  const overrides = flags
    .map(flag => ({ flag, state: stateFor(flag.key, tenantName) }))
    .filter(entry => STATE_META[entry.state].targeted);

  return (
    <Stack gap="md">
      <Divider label="Overrides" labelPosition="left" />
      {overrides.length === 0 ? (
        <Text size="sm" c="dimmed">
          No bespoke targeting — this tenant sits on defaults for every flag.
        </Text>
      ) : (
        <Stack gap={6}>
          {overrides.map(({ flag, state }) => (
            <Paper
              key={flag.key}
              withBorder
              p="xs"
              radius="sm"
              style={{ cursor: 'pointer' }}
              onClick={() => onSelectCell(flag.key, tenantName)}
            >
              <Group gap="sm" wrap="nowrap" justify="space-between">
                <Group gap="sm" wrap="nowrap">
                  <Badge size="sm" color={STATE_META[state].colour.split('.')[0]} variant="filled">
                    {STATE_META[state].onValue ? 'on' : 'off'}
                  </Badge>
                  <Text size="sm" ff="monospace">
                    {flag.key}
                  </Text>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export default function FlagDetailDrawer({
  selection,
  flags,
  tenants,
  stateFor,
  onSelectCell,
  onClose,
}: Props) {
  const title = selectionTitle(selection);
  const selectedTenant =
    selection !== null && selection.kind !== 'flag'
      ? tenantColumnFor(tenants, selection.tenantName)
      : null;

  return (
    <Drawer
      opened={selection !== null}
      onClose={onClose}
      title={title}
      position="right"
      size="lg"
      padding="md"
    >
      {selection?.kind === 'cell' && selectedTenant && (
        <CellBody flagKey={selection.flagKey} tenant={selectedTenant} stateFor={stateFor} />
      )}
      {selection?.kind === 'flag' && (
        <FlagBody flagKey={selection.flagKey} tenants={tenants} stateFor={stateFor} />
      )}
      {selection?.kind === 'tenant' && selectedTenant && (
        <TenantBody
          tenant={selectedTenant}
          flags={flags}
          stateFor={stateFor}
          onSelectCell={onSelectCell}
        />
      )}
    </Drawer>
  );
}
